export const CUSTOM_ERROR_STATUS = 'CUSTOM_ERROR';

type RuntimeConfig = {
  umsUrl?: string | null;
  creativeServerUrl?: string | null;
};

type RuntimeUrlKey = keyof RuntimeConfig;
type WithRuntimeUrlOptions = {
  localhostUrl?: string;
};
type RuntimeConfigResult = {
  data?: RuntimeConfig;
  error?: any;
};

let cachedRuntimeConfig: RuntimeConfig | null = null;
let runtimeConfigRequest: Promise<RuntimeConfigResult> | null = null;

const isLocalhost = () =>
  typeof window !== 'undefined' &&
  (window.location?.hostname === 'localhost' ||
    window.location?.hostname === '127.0.0.1');

export const resetRuntimeConfigCache = () => {
  cachedRuntimeConfig = null;
  runtimeConfigRequest = null;
};

const getRuntimeConfig = async (baseQuery: any): Promise<RuntimeConfigResult> => {
  if (cachedRuntimeConfig) {
    return { data: cachedRuntimeConfig };
  }

  if (!runtimeConfigRequest) {
    runtimeConfigRequest = baseQuery({
      url: '/api/v1/config',
      method: 'get',
    })
      .then((result: RuntimeConfigResult) => {
        if (result.data) {
          cachedRuntimeConfig = result.data;
        }

        return result;
      })
      .finally(() => {
        if (!cachedRuntimeConfig) {
          runtimeConfigRequest = null;
        }
      });
  }

  return runtimeConfigRequest;
};

export const withRuntimeUrl =
  (
    configKey: RuntimeUrlKey,
    missingMessage: string,
    buildRequest: (arg: any) => any,
    options: WithRuntimeUrlOptions = {},
  ) =>
  async (arg: any, _api: any, _extraOptions: any, baseQuery: any) => {
    const requestConfig = buildRequest(arg);

    if (options.localhostUrl && isLocalhost()) {
      return baseQuery({
        ...requestConfig,
        url: `${options.localhostUrl}${requestConfig.url}`,
      });
    }

    const runtimeConfigResult = await getRuntimeConfig(baseQuery);

    if (runtimeConfigResult.error) {
      return { error: runtimeConfigResult.error };
    }

    const runtimeConfig = (runtimeConfigResult.data || {}) as RuntimeConfig;
    const runtimeUrl = runtimeConfig[configKey];

    if (!runtimeUrl) {
      return {
        error: {
          status: CUSTOM_ERROR_STATUS,
          data: { message: missingMessage },
        },
      };
    }

    return baseQuery({
      ...requestConfig,
      url: `${runtimeUrl}${requestConfig.url}`,
    });
  };
